import { Component, OnInit } from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import { Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { filter, take, switchMap } from 'rxjs/operators';
import { AccountService } from 'src/app/services/account.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'upsc-claim-detail-verification',
  templateUrl: './claim-detail-verification.component.html',
  styleUrls: ['./claim-detail-verification.component.scss']
})
export class ClaimDetailVerificationComponent implements OnInit {

  emailAddress;
  userDetails;
  systemError = false;
  loading: boolean = false;
  workflow: string = '';

  constructor(
    private router: Router,
    private userService: UserService,
    private auth: AuthService,
    private accountService: AccountService
  ) {
    //This component should only be called during non-identity flows. Email must come from some session storage variable.
    const userDetailsJson = sessionStorage.getItem('userDetails');
    this.userDetails = userDetailsJson ? JSON.parse(userDetailsJson) : {}; //Commonly has email, claim number, policy number, and type
    this.emailAddress = this.userDetails.email;
  }

  ngOnInit() {
    this.gatherAllInfo();    
    this.determineLoginState();
  }

  //Sets returnURLs, defines workflow types
  gatherAllInfo() {
    try {  
      if (this.userDetails.returnURL !== undefined) {
        let returnUrl = this.userDetails.returnURL;
        if (returnUrl && returnUrl.includes('ssp')) {
          var parts = this.parsePath(returnUrl);
          this.workflow = parts[1].toLowerCase();
        } else {
          var parts = this.parsePath(returnUrl);
          if (parts[0].toLowerCase() === 'guest') {
            this.workflow = `claim-detail`;
          } else
            this.workflow = parts[0].toLowerCase();
        }
      } else if (this.userDetails.type !== undefined) {
        this.workflow = this.userDetails.type;
      } else {
        this.workflow = '';
      }
    } catch {
      this.workflow = '';
    }
  }

  //Check if user is authenticated
  determineLoginState() {
    this.loading = true;
    this.auth.isLoading$.pipe(
      filter(loading => !loading),
      take(1),
      switchMap(() => this.auth.idTokenClaims$),
      take(1)
    ).subscribe(
      response => {
        if (!response) {
          this.initiatePasswordlessAuth();
        } else {
          if (response.__raw && response.email) {
            if (response.email.toLowerCase() == this.emailAddress.toLowerCase()) {
              this.setTokenAndGetPolicy(response.__raw);
            } else {
              this.loading = false;
              this.systemError = true;
            }
          } else {
            this.loading = false;
            this.systemError = true;
          }
        }
      }, error => {
        this.initiatePasswordlessAuth();
      }
    );
  }

  initiatePasswordlessAuth() {
    this.auth.loginWithRedirect({
      authorizationParams: {
        connection: 'email',
        login_hint: this.emailAddress,  //Pre-fill the email with the email address the claim was filed on
        redirect_uri: `${environment.baseUrl.web}callback`,
        'ext-connect': 'email', //additional param to indicate template to hide "sign up" and "login" text
        'ext-type': 'hint'  //Disables the user to enter in their own email https://auth0.com/docs/customize/login-pages/universal-login/customize-templates#custom-query-parameters
        //redirect_uri: `${'http://localhost:4200/'}callback`
      },
      appState: { target: '/claim-detail-verification' }
    });
  }

  setTokenAndGetPolicy(id_token: string) {
    let request = {
      id_token: id_token,
      workflow: this.workflow
    }
    this.accountService.storePasswordlessToken(request).subscribe(
      response => {
        if (this.userDetails.type == 'happy-returns') {
          this.happyReturns();
        } else if (this.userDetails.type == 'self-service-portal') {
          this.ssp();
        } else if (this.userDetails.type == 'customized-claims-portal') { //ccp, and guest users
          this.ccp();
        }
      }, error => {
        //console.log("couldn't store token in database, show an error");
        this.loading = false;
        this.systemError = true;
      }
    );
  }

  happyReturns() {
    this.userService.getPolicySearch(this.userDetails.policyNumber).subscribe(
      policyData => {
        if (policyData?.message?.toLowerCase() === 'succeeded') {
          this.userService.updateUserInfo({ policyNumber: policyData.data.policyNumber, isClaimsActive: false });
          sessionStorage.setItem('policyDetails', JSON.stringify(policyData.data));
          let permissions: any[] = [];
          permissions.push('ClaimFile');
          this.userService.setUserPermissions(permissions);
          this.router.navigate([`/happy-returns/claims/detail`], { queryParams: { claimNumber: this.userDetails.claimNumber } });
        }
      }
    );
  }

  ssp() {
    let returnUrl = this.userDetails.returnURL;
    sessionStorage.setItem('self-service-landing', JSON.stringify({ email: this.userDetails.email }));
    this.router.navigate([returnUrl]);
  }

  //ccp, and guest users
  ccp() {
    let returnUrl = this.userDetails.returnURL;
    this.router.navigate([returnUrl]);
  }

  parsePath(path: string): string[] {
    return path.split("/").filter(Boolean);
  }
}
